import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React from 'react';
import {
  ChannelMap,
  GlobalChannelID,
  ReportRequest,
  ReportSchedule,
  ReportSpec,
} from '../../../../redux/reports/api/Report.model';
import { getPolyglot } from '../../../../i18n';
import { FormikProps, getIn } from 'formik';
import { Button, IconButton, Stack } from '@mui/material';
import useStateBoolean from '../../../../util/hooks/useStateBoolean';
import SelectChannelModal from './SelectChannelModal';
import EditChannelModal from './EditChannelModal';
import { useDevicesControlUnits } from '../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import { getTranslationNameByData } from '../../../../redux/monitoring/api/util/getLabelName';
import { useCUsTranslate } from '../../../../redux/CUTranslation/hook/useCUsTranslate';
import { useShowMessage } from '../../../../util/hooks';
import { DeleteIcon, EditIcon } from '../../../../theme/icons';
import { useGroupById } from '../../../../redux/groups/hooks/useGroupById';
import { getChannelsByComposableDevice } from '../../../../customized/fresh-watch/util/getChannelsByComposableDevice';
import useChannelsFromComposableDevice from '../../../../redux/composableDevice/hooks/useChannelsFromComposableDevice';

interface ChannelsMapTable {
  groupId: string | undefined;
  formik:
    | FormikProps<Omit<ReportSchedule, 'id'>>
    | FormikProps<Omit<ReportRequest, 'id'>>;
  variant: 'schedule' | 'report';
}
function getKey(variant: 'schedule' | 'report', key: string) {
  switch (variant) {
    case 'report':
      return `${key}`;
    case 'schedule':
      return `reportSpec.${key}`;
  }
}
function ChannelsMapTable(props: ChannelsMapTable) {
  const { formik, groupId, variant = 'schedule' } = props;
  const { data: composableChannels } = useChannelsFromComposableDevice(
    groupId ?? ''
  );
  const polyglot = getPolyglot();
  const showMessage = useShowMessage();
  const [devicesControlUnits, loadingDevicesControlUnits] =
    useDevicesControlUnits();

  const [CUsTranslate, loadingTranslations] = useCUsTranslate();
  const [isAddChannelOpen, openAddChannel, closeAddChannel] =
    useStateBoolean(false);
  const [isOpenEditChannel, openEditChannel, closeEditChannel] =
    useStateBoolean(false);
  const [channelToEdit, setChannelToEdit] = React.useState<
    ChannelMap | undefined
  >();

  const onCloseEditChannel = () => {
    setChannelToEdit(undefined);
    closeEditChannel();
  };

  const onSelectChannel = (channelIn: GlobalChannelID) => {
    const channel = {
      ...channelIn,
      minThreshold: undefined,
      maxThreshold: undefined,
    };
    // Find if channel is not already there:
    const existingChannel = getIn(
      formik.values,
      getKey(variant, 'channels')
    ).find(
      (item: ReportSpec['channels'][number]) =>
        item.channelID === channel.channelID &&
        item.deviceID === channel.deviceID &&
        item.cuID === channel.cuID
    );
    if (existingChannel) {
      showMessage(polyglot.t('reports.channel_map.already_added'), {
        variant: 'warning',
      });
      return;
    }
    // Add item on the list and open the modal to set max and min
    formik.setFieldValue(getKey(variant, 'channels'), [
      ...getIn(formik.values, getKey(variant, 'channels')),
      channel,
    ]);
    setChannelToEdit(channel);
    closeAddChannel();
    openEditChannel();
  };

  const onSaveEditChannel = (channel: ChannelMap) => {
    formik.setFieldValue(
      getKey(variant, 'channels'),
      getIn(formik.values, getKey(variant, 'channels')).map(
        (item: ChannelMap) =>
          channel.channelID === item.channelID &&
          channel.deviceID === item.deviceID &&
          channel.cuID === item.cuID
            ? channel
            : item
      )
    );
    onCloseEditChannel();
  };

  const onClickEditChannel = (channel: ChannelMap) => {
    setChannelToEdit(channel);
    openEditChannel();
  };

  const onClickDeleteChannel = (channel: ChannelMap) => {
    formik.setFieldValue(
      getKey(variant, 'channels'),
      getIn(formik.values, getKey(variant, 'channels')).filter(
        (item: ChannelMap) =>
          item.channelID !== channel.channelID ||
          item.deviceID !== channel.deviceID ||
          item.cuID !== channel.cuID
      )
    );
  };

  const allColumns: GridColDef<ChannelMap>[] = [
    {
      field: 'name',
      headerName: polyglot.t('composable_device.name'),
      minWidth: 150,
      flex: 1,
      valueGetter: (params) => {
        const channelRow = params.row;
        const foundChannel = composableChannels.find(
          (ch) =>
            ch.channelID === channelRow.channelID &&
            ch.cuID === channelRow.cuID &&
            ch.deviceID === channelRow.deviceID
        );
        if (foundChannel?.name) {
          return foundChannel.name;
        }
        // Fallback on the channel translation
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        const controlUnit = device?.controlUnits?.find(
          (cu) => cu.id === params.row.cuID
        );
        const channel = controlUnit?.channels.find(
          (ch) => ch.id === params.row.channelID
        );
        const primaryText = getTranslationNameByData(
          channel?.name,
          channel?.id,
          CUsTranslate,
          params.row.deviceID,
          params.row.cuID
        );
        return primaryText;
      },
    },
    {
      field: 'deviceID',
      headerName: polyglot.t('group.devices.device'),
      minWidth: 150,
      valueGetter: (params) => {
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        return device ? device.deviceName : '';
      },
      flex: 1,
    },
    {
      field: 'cuID',
      headerName: polyglot.t('device.controlUnit.controller'),
      valueGetter: (params) => {
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        const controlUnit = device?.controlUnits?.find(
          (cu) => cu.id === params.row.cuID
        );
        return controlUnit ? controlUnit.name : params.row.cuID;
      },
      minWidth: 150,
      flex: 1,
    },

    {
      field: 'channelID',
      headerName: polyglot.t('reports.channel_map.channel'),
      valueGetter: (params) => {
        const device = devicesControlUnits.find(
          (item) => item.deviceId === params.row.deviceID
        );
        const controlUnit = device?.controlUnits?.find(
          (cu) => cu.id === params.row.cuID
        );
        const channel = controlUnit?.channels.find(
          (ch) => ch.id === params.row.channelID
        );
        const primaryText = getTranslationNameByData(
          channel?.name,
          channel?.id,
          CUsTranslate,
          params.row.deviceID,
          params.row.cuID
        );
        return primaryText;
      },
      minWidth: 120,
      flex: 1,
    },
    {
      field: 'minThreshold',
      headerName: polyglot.t('reports.channel_map.minThreshold'),
      minWidth: 120,
    },
    {
      field: 'maxThreshold',
      headerName: polyglot.t('reports.channel_map.maxThreshold'),
      minWidth: 120,
    },
    {
      field: 'actions',
      headerName: polyglot.t('reports.table.actions'),
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            onClick={() => onClickEditChannel(params.row)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onClickDeleteChannel(params.row)}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];
  const columns = composableChannels.length
    ? allColumns.filter(
        (c) => !['deviceID', 'cuID', 'channelID'].includes(c.field)
      )
    : allColumns.filter((c) => c.field !== 'name');

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        alignItems="baseline"
        justifyContent="flex-end"
        paddingBottom={2}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={openAddChannel}
        >
          {polyglot.t('reports.channel_map.add_new_channel')}
        </Button>
      </Stack>
      <DataGrid
        rows={getIn(formik.values, getKey(variant, 'channels'))}
        columns={columns}
        disableRowSelectionOnClick
        getRowId={(row) =>
          `${row.deviceID}-${row.cuID}-${row.channelID}-${row.maxThreshold}-${row.minThreshold}`
        }
        loading={loadingDevicesControlUnits && loadingTranslations}
        sx={{
          maxWidth: '100%',
        }}
      />
      <SelectChannelModal
        groupId={groupId}
        open={isAddChannelOpen}
        onClose={closeAddChannel}
        onSelectItem={onSelectChannel}
      />
      <EditChannelModal
        open={isOpenEditChannel}
        onClose={onCloseEditChannel}
        onSaveItem={onSaveEditChannel}
        channel={channelToEdit}
        groupId={props.groupId}
      />
    </>
  );
}

export default ChannelsMapTable;
